import { Package } from 'lucide-react';

/**
 * Inline preview of products inside a line-item document (invoice/bill/estimate etc.)
 * Renders a compact, single-line summary such as:
 *   📦  Dell Laptop × 2 · Logitech Mouse × 5 · Custom Item × 1  (+3 more)
 *
 * Props:
 *   lineItems: array – the document's line_items field
 *   productMap: object – map of product_id → product name (or full product record)
 *   max:       number  – max items to render before collapsing into "+N more"
 */
export const ProductsPreview = ({ lineItems = [], productMap = {}, max = 4 }) => {
  if (!lineItems || lineItems.length === 0) return null;

  const resolveName = (li) => {
    if (li.product_id) {
      const p = productMap[li.product_id];
      if (p) return typeof p === 'string' ? p : (p.name || p.product_name || li.description || 'Product');
    }
    return li.description || 'Custom Item';
  };

  const fmtQty = (q) => {
    if (q === undefined || q === null) return '';
    const n = Number(q);
    if (Number.isNaN(n)) return '';
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  };

  const visible = lineItems.slice(0, max);
  const hiddenCount = lineItems.length - visible.length;

  return (
    <div className="flex items-start gap-2 pt-1 text-xs text-slate-500">
      <Package className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-slate-400" />
      <div className="flex-1 min-w-0">
        <span className="truncate">
          {visible.map((li, i) => (
            <span key={i}>
              <span className="text-slate-600 font-medium">{resolveName(li)}</span>
              {li.quantity != null && (
                <span className="text-slate-400"> × {fmtQty(li.quantity)}</span>
              )}
              {i < visible.length - 1 && <span className="text-slate-300 mx-1.5">·</span>}
            </span>
          ))}
          {hiddenCount > 0 && (
            <span className="text-slate-400 ml-1.5">(+{hiddenCount} more)</span>
          )}
        </span>
      </div>
    </div>
  );
};
